import type { ReactNode } from "react";
import { Bot, Folder, Lock } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { cn } from "~/lib/utils";

const ROW_ICONS = {
  dataset: Folder,
  agent: Bot,
} as const;

export interface PermissionWarningRow {
  key: string;
  kind: keyof typeof ROW_ICONS;
  // Who the row is about, shown in bold ahead of the text.
  subject?: string;
  text: string;
  // Rendered beside the text, e.g. a button to copy the affected members' emails.
  action?: ReactNode;
}

interface PermissionWarningCardProps {
  rows: PermissionWarningRow[];
  footer?: string;
  className?: string;
}

// One card per place a permissions-consistency check surfaces, with a row per
// issue so several findings read as a list rather than a run of sentences.
export const PermissionWarningCard = ({ rows, footer, className }: PermissionWarningCardProps) => (
  <Alert variant="warning" className={cn("px-3 py-2", className)}>
    <AlertTitle className="text-[13px] font-semibold">
      <span className="flex items-center gap-2">
        <Lock className="size-3.5 shrink-0 text-amber-800 dark:text-amber-300" aria-hidden="true" />
        Access needed
      </span>
    </AlertTitle>
    <AlertDescription className="w-full justify-items-stretch gap-0">
      <ul className="mt-2 divide-y divide-amber-200 border-t border-amber-200 dark:divide-amber-500/30 dark:border-amber-500/30">
        {rows.map(({ key, kind, subject, text, action }) => {
          const Icon = ROW_ICONS[kind];
          return (
            // With a footer, a divider follows the last row, so it keeps its padding.
            <li
              key={key}
              className={cn("flex items-start gap-2 py-2 text-xs", !footer && "last:pb-0")}
            >
              <Icon className="size-3.5 shrink-0" aria-hidden="true" />
              <span className="flex-1">
                {subject && (
                  <strong className="font-semibold text-amber-900 dark:text-amber-400">
                    {subject}{" "}
                  </strong>
                )}
                {text}
              </span>
              {action}
            </li>
          );
        })}
      </ul>
      {footer && (
        <p className="border-t border-amber-200 pt-2 text-xs font-medium text-amber-900 dark:border-amber-500/30 dark:text-amber-400">
          {footer}
        </p>
      )}
    </AlertDescription>
  </Alert>
);
